"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useQueryLifecycle = void 0;
var React = require("react");
var hooks_1 = require("../../hooks");
var store_1 = require("../../store");
var connectorStore_1 = require("../connectorStore");
var dependency_1 = require("./dependency");
/**
 * Abstracted effect hooks (and a refresh callback) related to query lifecycle changes.  Extracted so that
 * we can use them for both bulk query, and paginated bulk query.
 *
 * @returns A refresh callback that re-fetches all dependencies (unless wait is true).
 */
exports.useQueryLifecycle = function (_a) {
    var dependencies = _a.dependencies, wait = _a.wait, call = _a.call;
    // Essentially we want to call a dependency any time we encounter a dependencyKey we didn't see on the last go
    // around (and it meets the refreshDependencyOnMount criteria).  To do this just store a reference to the previous
    // dependency keys and filter out the dependencies that were there on the last render.  Initialize the previous
    // keys to [], so that on mount we evaluate calling every dependency.
    //
    // The one exception is if the 'wait' boolean transitions from true to false -
    // in that case we should call everything even if we have seen the keys before
    var wasWaiting = hooks_1.usePrevious(wait, false);
    var doneWaiting = wasWaiting && !wait;
    var previousDependencyKeys = hooks_1.usePrevious(dependencies.map(function (d) { return d.dependencyKey; }), []);
    var depsToCall = doneWaiting
        ? dependencies
        : dependencies.filter(function (d) { return !previousDependencyKeys.includes(d.dependencyKey); });
    var store = store_1.useStoreContext().store;
    // Call any dependencies with a new dependencyKey, so long as 'wait' isn't true, and the dependency should
    // be refreshed
    React.useEffect(function () {
        if (!wait) {
            depsToCall.forEach(function (currentDep) {
                // We need to get the most up-to-date state before we decide if we should fetch the
                // data. If two components mount in the same render cycle they will have the same
                // view of the current state stored in the dependency.  If the first one decides to
                // fetch, the store is updated but the useEffects for the other components still have the
                // outdated data so they would decide to fetch too.  This ensures that we are using the most
                // up to date data when we decide to fetch or not.
                var dependencyWithUpdatedState = __assign(__assign({}, currentDep), { currentState: connectorStore_1.normalizedGet(store, currentDep.dependencyKey) });
                if (dependency_1.shouldRefreshDependencyOnMount(dependencyWithUpdatedState)) {
                    call(currentDep);
                }
            });
        }
    }, [store, depsToCall, wait, call]);
    var refresh = React.useCallback(function (force) {
        // if the dependency isn't met, refresh shouldn't make the calls
        if (force || !wait) {
            dependencies.forEach(call);
        }
    }, [dependencies, wait, call]);
    return refresh;
};
exports.default = exports.useQueryLifecycle;
//# sourceMappingURL=useQueryLifecycle.js.map